import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, Download, Trash2, ScrollText, Loader2, X, ExternalLink, Keyboard, ShieldAlert, DownloadCloud, RefreshCw, Eraser } from "lucide-react";

function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

function Highlight({ text, terms }) {
  const list = (Array.isArray(terms) ? terms : [terms]).filter((t) => t && t.length);
  if (!list.length) return <>{text}</>;
  const parts = String(text).split(new RegExp(`(${list.map(escapeRe).join("|")})`, "gi"));
  return (
    <>
      {parts.map((p, i) =>
        list.some((t) => t.toLowerCase() === p.toLowerCase())
          ? <mark key={i} className="log-highlight">{p}</mark> : <span key={i}>{p}</span>
      )}
    </>
  );
}

// tampilkan waktu sebagai WIB (GMT+7); anggap UTC bila string tanpa offset
function fmtWIB(ts) {
  if (!ts) return "";
  let s = String(ts);
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s += "Z";
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(ts).slice(0, 19).replace("T", " ");
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Jakarta", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).format(d);
}

function toCsv(rows, deviceName) {
  const header = ["line_no", "waktu_wib", "pc", "window", "text"];
  return [header.join(",")].concat(
    rows.map((r) => [r.line_no, fmtWIB(r.ts), r.device_name || deviceName, r.window || "", `"${(r.text || "").replace(/"/g, '""')}"`].join(","))
  ).join("\n");
}
function download(csv, filename) {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function LogViewer() {
  const { isMaster } = useAuth();
  const [params] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(params.get("device") || "");
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState("");
  const [mode, setMode] = useState("all"); // all | badwords
  const [logs, setLogs] = useState([]);
  const [badwordList, setBadwordList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const [cleanupDays, setCleanupDays] = useState("60");
  const [pendingDate, setPendingDate] = useState("");

  const [keyword, setKeyword] = useState("");
  const [scopePc, setScopePc] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [searchData, setSearchData] = useState(null);

  useEffect(() => {
    api.get("/devices").then(({ data }) => {
      setDevices(data);
      if (!deviceId && data.length) setDeviceId(data[0].id);
    });
  }, []);

  const loadDates = (id) => api.get(`/devices/${id}/dates`).then(({ data }) => { setDates(data.dates); return data.dates; });

  useEffect(() => {
    if (!deviceId) return;
    setLogs([]);
    loadDates(deviceId).then((ds) => {
      setDate(pendingDate && ds.includes(pendingDate) ? pendingDate : (ds[0] || ""));
      setPendingDate("");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  useEffect(() => {
    if (searchMode || !deviceId || !date) return;
    setLoading(true);
    const url = mode === "badwords" ? `/devices/${deviceId}/badwords` : `/devices/${deviceId}/logs`;
    api.get(url, { params: { date } })
      .then(({ data }) => { setLogs(data.logs); setBadwordList(data.keywords || []); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, date, mode, searchMode, tick]);

  // auto-scroll ke kecocokan pertama saat membuka log dari hasil pencarian
  useEffect(() => {
    if (searchMode || !keyword || !logs.length) return;
    const t = setTimeout(() => {
      const el = document.querySelector(".log-highlight");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(t);
  }, [logs, keyword, searchMode]);

  const runSearch = async () => {
    if (keyword.trim().length < 2) { toast.error("Keyword minimal 2 karakter"); return; }
    setSearchMode(true);
    setLoading(true);
    const p = { q: keyword.trim() };
    if (scopePc !== "all") p.pc = scopePc;
    if (fromDate) p.from_date = fromDate;
    if (toDate) p.to_date = toDate;
    try {
      const { data } = await api.get("/search", { params: p });
      setSearchData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const clearSearch = () => { setSearchMode(false); setKeyword(""); setSearchData(null); };

  const openFromSearch = (g) => {
    setSearchMode(false); setMode("all");
    if (g.device_id === deviceId) {
      setDate(g.date);
    } else {
      setPendingDate(g.date);
      setDeviceId(g.device_id);
    }
  };

  const downloadGroup = async (g) => {
    try {
      const { data } = await api.get(`/devices/${g.device_id}/logs`, { params: { date: g.date } });
      download(toCsv(data.logs, g.device_name), `keylog_${g.device_name}_${g.date}.csv`);
      toast.success("CSV diunduh");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportCurrent = () => {
    if (!logs.length) { toast.error("Tidak ada data untuk diekspor"); return; }
    download(toCsv(logs, activeDevice?.name), `keylog_${activeDevice?.name}_${date}${mode === "badwords" ? "_badwords" : ""}.csv`);
    toast.success("CSV diunduh");
  };

  const downloadAll = async () => {
    try {
      const { data } = await api.get(`/devices/${deviceId}/logs/all`);
      if (!data.logs.length) { toast.error("Belum ada log"); return; }
      download(toCsv(data.logs, activeDevice?.name), `keylog_${activeDevice?.name}_semua.csv`);
      toast.success(`${data.count} baris diunduh`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteDayLogs = async () => {
    try {
      const { data } = await api.delete(`/devices/${deviceId}/logs`, { params: { date } });
      toast.success(`${data.deleted} baris log dihapus`);
      const ds = await loadDates(deviceId);
      setDate(ds[0] || "");
      setLogs([]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doCleanup = async () => {
    try {
      const params = cleanupDays === "all" ? { all: true } : { days: Number(cleanupDays) };
      const { data } = await api.delete("/logs/cleanup", { params });
      toast.success(`${data.deleted} baris log lama dihapus`);
      if (deviceId) { const ds = await loadDates(deviceId); setDate(ds[0] || ""); }
      setLogs([]); setTick((t) => t + 1);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const activeDevice = devices.find((d) => d.id === deviceId);
  const highlightTerms = searchMode ? [] : (mode === "badwords" ? badwordList : [keyword]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2">
          <Keyboard className="text-[#3B82F6]" size={30} /> Log Keystroke
        </h1>
        <p className="text-[#9CA3AF] mt-1 text-sm">Rekaman ketikan per aplikasi. Lihat per PC/tanggal, filter badwords, atau cari keyword di semua log.</p>
      </div>

      <div className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 space-y-3 mb-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <Select value={deviceId} onValueChange={setDeviceId}>
            <SelectTrigger data-testid="log-device-select" className="bg-[#0B0F17] border-[#1F2937] text-white lg:w-56"><SelectValue placeholder="Pilih PC" /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#1F2937] text-white">
              {devices.map((d) => <SelectItem key={d.id} value={d.id} className="focus:bg-[#1F2937]">{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={date} onValueChange={setDate} disabled={searchMode}>
            <SelectTrigger data-testid="log-date-picker" className="bg-[#0B0F17] border-[#1F2937] text-white lg:w-44"><SelectValue placeholder="Pilih tanggal" /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#1F2937] text-white">
              {dates.length === 0 ? <SelectItem value="none" disabled>Belum ada log</SelectItem>
                : dates.map((d) => <SelectItem key={d} value={d} className="focus:bg-[#1F2937]">{d}</SelectItem>)}
            </SelectContent>
          </Select>

        </div>

        <div className="flex flex-col lg:flex-row gap-2 pt-1 border-t border-[#1F2937]">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <Input data-testid="log-search-input" value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="Cari kata di semua log (mis. berlin)..." className="bg-[#0B0F17] border-[#1F2937] text-white pl-9" />
          </div>
          <Select value={scopePc} onValueChange={setScopePc}>
            <SelectTrigger data-testid="search-pc-select" className="bg-[#0B0F17] border-[#1F2937] text-white lg:w-40"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#1F2937] text-white">
              <SelectItem value="all" className="focus:bg-[#1F2937]">Semua PC</SelectItem>
              {devices.map((d) => <SelectItem key={d.id} value={d.id} className="focus:bg-[#1F2937]">{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="search-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-[#0B0F17] border-[#1F2937] text-white lg:w-40" title="Dari tanggal" />
          <Input data-testid="search-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-[#0B0F17] border-[#1F2937] text-white lg:w-40" title="Sampai tanggal" />
          <Button data-testid="log-search-button" onClick={runSearch} className="bg-[#06B6D4] hover:bg-[#0891B2] text-white">Cari</Button>
          {searchMode && <Button data-testid="clear-search-button" onClick={clearSearch} variant="ghost" size="icon" className="text-[#9CA3AF] hover:text-white"><X size={18} /></Button>}
        </div>
      </div>

      {searchMode ? (
        <SearchResults data={searchData} keyword={keyword} loading={loading} onOpen={openFromSearch} onDownload={downloadGroup} />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Badge data-testid="log-line-count" className={mode === "badwords" ? "bg-[#EF4444]/15 text-[#F87171] border-[#EF4444]/30" : "bg-[#1F2937] text-[#9CA3AF] border-[#374151]"}>
                {logs.length} {mode === "badwords" ? "badwords" : "baris ketikan"}
              </Badge>
              {mode === "badwords" && badwordList.length > 0 && <span className="text-xs text-[#6B7280]">keyword aktif: {badwordList.join(", ")}</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="refresh-button" onClick={() => { if (deviceId) loadDates(deviceId); setTick((t) => t + 1); }} variant="outline" className="h-9 bg-transparent border-[#1F2937] text-[#9CA3AF] hover:text-white hover:bg-[#1F2937]"><RefreshCw size={15} className="mr-1.5" /> Refresh</Button>
              <Button data-testid="export-csv-button" onClick={exportCurrent} variant="outline" className="h-9 bg-transparent border-[#1F2937] text-[#9CA3AF] hover:text-white hover:bg-[#1F2937]"><Download size={15} className="mr-1.5" /> Export CSV</Button>
              <Button data-testid="download-all-button" onClick={downloadAll} variant="outline" className="h-9 bg-transparent border-[#1F2937] text-[#9CA3AF] hover:text-white hover:bg-[#1F2937]"><DownloadCloud size={15} className="mr-1.5" /> Semua</Button>
              {isMaster && date && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="delete-log-button" variant="outline" className="h-9 bg-transparent border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 size={15} className="mr-1.5" /> Hapus</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#111827] border-[#1F2937] text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Hapus log {activeDevice?.name} tanggal {date}?</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#9CA3AF]">Ketikan pada tanggal ini akan dihapus permanen.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-[#1F2937] border-[#374151] text-white hover:bg-[#374151]">Batal</AlertDialogCancel>
                      <AlertDialogAction onClick={deleteDayLogs} className="bg-[#EF4444] hover:bg-[#DC2626]">Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {isMaster && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="cleanup-logs-button" variant="outline" className="h-9 bg-transparent border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10"><Eraser size={15} className="mr-1.5" /> Bersihkan Lama</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#111827] border-[#1F2937] text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Bersihkan Log Lama</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#9CA3AF]">Hapus log lama dari SEMUA PC untuk menghemat penyimpanan. Akun, device & API Key tetap aman.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-1">
                      <Select value={cleanupDays} onValueChange={setCleanupDays}>
                        <SelectTrigger data-testid="cleanup-range-select" className="bg-[#0B0F17] border-[#1F2937] text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#1F2937] text-white">
                          <SelectItem value="30" className="focus:bg-[#1F2937]">Lebih tua dari 30 hari</SelectItem>
                          <SelectItem value="60" className="focus:bg-[#1F2937]">Lebih tua dari 60 hari</SelectItem>
                          <SelectItem value="90" className="focus:bg-[#1F2937]">Lebih tua dari 90 hari</SelectItem>
                          <SelectItem value="all" className="focus:bg-[#1F2937] text-[#F87171]">Hapus SEMUA log</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-[#1F2937] border-[#374151] text-white hover:bg-[#374151]">Batal</AlertDialogCancel>
                      <AlertDialogAction data-testid="cleanup-confirm-button" onClick={doCleanup} className="bg-[#F59E0B] hover:bg-[#D97706] text-black">Bersihkan</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>

          <div className="bg-[#0B0F17] border border-[#1F2937] rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#3B82F6]" size={28} /></div>
            ) : logs.length === 0 ? (
              <div className="text-center py-24 text-[#6B7280]">
                {mode === "badwords" ? <ShieldAlert size={40} className="mx-auto mb-3 opacity-40" /> : <ScrollText size={40} className="mx-auto mb-3 opacity-40" />}
                {mode === "badwords" ? "Tidak ada badwords terdeteksi pada tanggal ini." : "Tidak ada ketikan untuk ditampilkan."}
              </div>
            ) : (
              <div className="font-mono text-xs sm:text-sm max-h-[60vh] overflow-auto p-2">
                {logs.map((l, i) => (
                  <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.004, 0.25) }}
                    className={`rounded-lg border-l-2 px-3 py-2 mb-1.5 ${mode === "badwords" ? "border-[#EF4444] bg-[#EF4444]/5" : "border-[#1F2937] bg-[#111827]/50 hover:bg-[#111827]"}`}>
                    <div className="flex items-center gap-2 text-[11px] text-[#6B7280] mb-0.5">
                      <span className="text-[#374151]">#{l.line_no}</span>
                      <span>[{fmtWIB(l.ts)} WIB]</span>
                      <span className="text-[#60A5FA]">({activeDevice?.name})</span>
                      <span className="px-1.5 rounded bg-[#1F2937] text-[#22D3EE]">{l.window || "—"}</span>
                    </div>
                    <div className="text-[#E5E7EB] break-words"><Highlight text={l.text} terms={highlightTerms} /></div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SearchResults({ data, keyword, loading, onOpen, onDownload }) {
  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#3B82F6]" size={28} /></div>;
  if (!data) return null;
  return (
    <div>
      <div className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 mb-4 text-sm">
        <span className="text-[#9CA3AF]">Kata kunci: </span><span className="text-[#FEF08A] font-mono">{data.query}</span>
        <span className="mx-3 text-[#374151]">·</span>
        <span data-testid="log-search-match-count" className="text-white font-semibold">{data.total_matches} kecocokan</span>
        <span className="text-[#9CA3AF]"> di {data.group_count} log</span>
      </div>
      {data.groups.length === 0 ? (
        <div className="text-center py-24 text-[#6B7280] bg-[#0B0F17] border border-[#1F2937] rounded-xl"><Search size={40} className="mx-auto mb-3 opacity-40" />Tidak ada hasil untuk keyword ini.</div>
      ) : (
        <div className="space-y-3">
          {data.groups.map((g, idx) => (
            <motion.div key={`${g.device_id}-${g.date}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
              data-testid={`search-group-${g.device_id}-${g.date}`} className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 flex flex-col lg:flex-row gap-4">
              <div className="lg:w-48 shrink-0">
                <div className="font-display font-bold text-white">{g.device_name}</div>
                <div className="text-xs text-[#9CA3AF] mt-0.5">{g.date}</div>
                <Badge className="mt-2 bg-[#3B82F6]/15 text-[#60A5FA] border-[#3B82F6]/30">{g.hit_count} hit</Badge>
              </div>
              <div className="flex-1 font-mono text-xs space-y-1 min-w-0">
                {g.samples.map((s, i) => (
                  <div key={i} className="text-[#D1D5DB] break-words">
                    <span className="text-[#374151]">[{s.line_no}]</span> <span className="text-[#22D3EE]">{s.window}</span> <Highlight text={s.text} terms={[keyword]} />
                  </div>
                ))}
              </div>
              <div className="flex lg:flex-col gap-2 shrink-0">
                <Button data-testid={`open-log-${g.device_id}-${g.date}`} onClick={() => onOpen(g)} size="sm" className="h-8 bg-[#1F2937] hover:bg-[#374151] text-white"><ExternalLink size={14} className="mr-1" /> Buka Log</Button>
                <Button data-testid={`download-log-${g.device_id}-${g.date}`} onClick={() => onDownload(g)} size="sm" variant="outline" className="h-8 bg-transparent border-[#1F2937] text-[#9CA3AF] hover:text-white hover:bg-[#1F2937]"><Download size={14} className="mr-1" /> Unduh</Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
